import { useState, useRef } from "react";
import PropTypes from "prop-types";

export default function ThemeButton({
  label = "SIGN UP",
  onClick,
  className = "",
  primaryColor = "bg-primary",
  secondaryColor = "bg-accentSecondary",
  textColor = "#ffffff",
  hoverTextColor = "#160033",
  type = "button",
  disabled = false
}) {
  const [hovered, setHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const buttonRef = useRef(null);

  const handleMouseEnter = (e) => {
    const button = buttonRef.current;
    const rect = button.getBoundingClientRect();
    // Calculate mouse position relative to button center
    const x = ((e.clientX - rect.left) / button.clientWidth) * 100;
    const y = ((e.clientY - rect.top) / button.clientHeight) * 100;
    setMousePosition({ x, y });
    setHovered(true);
  };

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        type={type}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHovered(false)}
        disabled={disabled}
        className={`relative inline-flex items-center justify-center
                   sm:px-8 sm:py-4 rounded-full font-black tracking-tight
                   px-6 py-3 sm:text-base text-sm
                   ${primaryColor} select-none 
                   ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
                   ${className}`}
      >
        {/* Sweep circle */}
        <div
          className="absolute inset-0 overflow-hidden rounded-full"
          style={{
            clipPath: "circle(0% at 50% 50%)",
            transition: "clip-path 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
            ...(hovered && {
              clipPath: `circle(150% at ${mousePosition.x}% ${mousePosition.y}%)`,
            }),
          }}
        >
          <div className={`absolute inset-0 ${secondaryColor}`} />
        </div>

        {/* Rays - now positioned relative to wrapper */}
        {[{ deg: "-95", transx: '-25', width: '15', left: '9', bottom: '10' },
        { deg: "-45", transx: '-25', width: '15', left: '10', bottom: '11' },
        { deg: "0", transx: '-25', width: '15', left: '9', bottom: '10' }]
          .map((key, i) => (
            <span
              key={i}
              className="absolute w-0 h-[3px] bg-[#160033]
                     origin-left transition-all duration-300 ease-out rounded-xl"
              style={{
                transform: `rotate(${key.deg}deg) translateX(${key.transx}px)`,
                width: hovered ? `${key.width}px` : "0px",
                opacity: hovered ? 1 : 0,
                transitionDelay: `${i * 90}ms`,
                left: `${key.left}px`, // Moved further left
                bottom: `${key.bottom}px`, // Moved further down
                zIndex: -1, // Ensure rays appear behind button
              }}
            />
          ))}







        {/* Letters with loading state */}
        {label.split("").map((ch, idx) => (
          <span
            key={idx}
            className="relative inline-block transition-all duration-200 font-display"
            style={{
              transitionDelay: `${250 + idx * 30}ms`,
              transform: `translateY(${hovered && !disabled ? "-2px" : "0"})`,
              color: hovered && !disabled ? hoverTextColor : textColor,
              marginRight: ch === " " ? "0.3em" : "0.1rem",
              opacity: disabled ? 0.7 : 1
            }}
          >
            {ch === " " ? "\u00A0" : ch} {/* Use non-breaking space for spaces */}
          </span>
        ))}
      </button>
    </div>
  );
}

ThemeButton.propTypes = {
  label: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  textColor: PropTypes.string,
  hoverTextColor: PropTypes.string,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
  disabled: PropTypes.bool
};
